/**
 * Audio feedback types for scanner app
 */
export type AudioFeedbackType =
  | 'positionScan'    // A1 - Zdrojová/cieľová pozícia OK
  | 'productScan'     // B1 - Produkt pridaný (ultra short)
  | 'requireInput'    // C5 - Vyžaduje pozornosť/vstup
  | 'blocked'         // D1 - Sken zablokovaný
  | 'invalidCode'     // E3 - Neplatný/nerozpoznaný kód
  | 'noPosition'      // F5 - Chýba aktívna pozícia
  | 'validation'      // G6 - Chyba validácie/nedostatok
  | 'tooMuch'         // H4 - Príliš veľa
  // Legacy compatibility
  | 'success'
  | 'error';

// Shared AudioContext for better performance
let sharedAudioContext: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (sharedAudioContext && sharedAudioContext.state !== 'closed') {
    return sharedAudioContext;
  }

  const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
  if (!AudioContextClass) {
    console.warn('Web Audio API not supported');
    return null;
  }

  sharedAudioContext = new AudioContextClass();
  return sharedAudioContext;
}

// Unlock AudioContext on first user interaction (required for iOS)
if (typeof document !== 'undefined') {
  ['mousedown', 'keydown', 'touchstart'].forEach(eventType => {
    document.addEventListener(eventType, function unlock() {
      const ctx = getAudioContext();
      if (ctx && ctx.state === 'suspended') {
        ctx.resume();
      }
      document.removeEventListener(eventType, unlock);
    }, { once: true });
  });
}

interface Tone {
  f: number;  // frequency
  d: number;  // duration
  g?: number; // gap (time until next tone starts)
}

function playTone(freq: number, duration: number, type: OscillatorType = 'sine', startTime: number = 0, volume: number = 0.3): void {
  const audioContext = getAudioContext();
  if (!audioContext) return;

  if (audioContext.state === 'suspended') {
    audioContext.resume();
  }

  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();

  oscillator.type = type;
  oscillator.frequency.value = freq;
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);

  const now = audioContext.currentTime + startTime;
  gainNode.gain.setValueAtTime(volume, now);
  gainNode.gain.exponentialRampToValueAtTime(0.01, now + duration);

  oscillator.start(now);
  oscillator.stop(now + duration);
}

function playSequence(tones: Tone[], type: OscillatorType = 'sine', volume: number = 0.3): void {
  let time = 0;
  tones.forEach(tone => {
    playTone(tone.f, tone.d, type, time, volume);
    time += tone.g ?? tone.d;
  });
}

/**
 * Sound definitions based on ReceivedOrderPicking
 */
const soundDefinitions: Record<string, () => void> = {
  // A1 - Position scan success (dvojtón hore 660→880 Hz)
  positionScan: () => playSequence([{f: 660, d: 0.08, g: 0.1}, {f: 880, d: 0.12}], 'sine'),

  // B1 - Product scan success (ultra short 50ms, 1000 Hz)
  productScan: () => playTone(1000, 0.05, 'sine'),

  // C5 - Require input / attention (700→600→700 Hz)
  requireInput: () => playSequence([{f: 700, d: 0.07, g: 0.09}, {f: 600, d: 0.07, g: 0.09}, {f: 700, d: 0.1}], 'sine'),

  // D1 - Blocked (300→200 Hz)
  blocked: () => playSequence([{f: 300, d: 0.08, g: 0.1}, {f: 200, d: 0.12}], 'sine'),

  // E3 - Invalid/unknown code (380→280 Hz triangle)
  invalidCode: () => playSequence([{f: 380, d: 0.1, g: 0.12}, {f: 280, d: 0.15}], 'triangle'),

  // F5 - No position (350→250 Hz triangle)
  noPosition: () => playSequence([{f: 350, d: 0.1, g: 0.12}, {f: 250, d: 0.15}], 'triangle'),

  // G6 - Validation error / insufficient stock (250 Hz x3 slow)
  validation: () => playSequence([{f: 250, d: 0.1, g: 0.2}, {f: 250, d: 0.1, g: 0.2}, {f: 250, d: 0.1}], 'sine'),

  // H4 - Too much packed (380→280 Hz x2)
  tooMuch: () => playSequence([{f: 380, d: 0.08, g: 0.1}, {f: 280, d: 0.08, g: 0.1}, {f: 380, d: 0.08, g: 0.1}, {f: 280, d: 0.1}], 'sine'),

  // Legacy compatibility
  success: () => playTone(1000, 0.05, 'sine'), // Same as productScan
  error: () => playSequence([{f: 350, d: 0.1, g: 0.12}, {f: 250, d: 0.15}], 'triangle'), // Same as noPosition
};

/**
 * Play audio feedback using Web Audio API (generated tones)
 *
 * @param type - Type of feedback sound to play
 */
export const playAudioFeedback = (type: AudioFeedbackType): void => {
  try {
    const playSound = soundDefinitions[type];
    if (playSound) {
      playSound();
    } else {
      console.warn(`Unknown audio feedback type: ${type}`);
    }
  } catch (err) {
    console.warn(`Error playing ${type} sound:`, err);
  }
};
